import React, { useState, useEffect, useRef } from "react";
import { KTIcon } from "../../../_metronic/helpers";
import { useQuickActions } from "../../hooks/useQuickActions";
import { getQuickActionsByCategory } from "../../config/QuickActionConfig";

const SIDEBAR_STATE_KEY = "bedrock_sidebar_default_state";
const TABLE_PAGE_SIZE_KEY = "bedrock_table_page_size";

type SidebarState = "expanded" | "collapsed";
type TablePageSize = 10 | 25 | 50 | 100;

const UISettingsPage: React.FC = () => {
  const {
    selectedActionIds,
    selectedActions,
    addAction,
    addSeparator,
    removeAction,
    reorderActions,
    isActionSelected,
    isActionPermanent,
    canAddMore,
    maxActions,
  } = useQuickActions();

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const draggedItemRef = useRef<string | null>(null);

  const [sidebarState, setSidebarState] = useState<SidebarState>(() => {
    const stored = localStorage.getItem(SIDEBAR_STATE_KEY);
    return (stored as SidebarState) || "expanded";
  });

  const [tablePageSize, setTablePageSize] = useState<TablePageSize>(() => {
    const stored = localStorage.getItem(TABLE_PAGE_SIZE_KEY);
    return stored ? (parseInt(stored) as TablePageSize) : 10;
  });

  const actionsByCategory = getQuickActionsByCategory();

  const handleSidebarStateChange = (newState: SidebarState) => {
    setSidebarState(newState);
    localStorage.setItem(SIDEBAR_STATE_KEY, newState);
    if (newState === "collapsed") {
      document.body.setAttribute("data-kt-app-sidebar-minimize", "on");
    } else {
      document.body.removeAttribute("data-kt-app-sidebar-minimize");
    }
  };

  const handleTablePageSizeChange = (size: TablePageSize) => {
    setTablePageSize(size);
    localStorage.setItem(TABLE_PAGE_SIZE_KEY, size.toString());
  };

  useEffect(() => {
    if (sidebarState === "collapsed") {
      document.body.setAttribute("data-kt-app-sidebar-minimize", "on");
    }
  }, []);

  const handleToggleAction = (actionId: string) => {
    if (isActionSelected(actionId)) {
      removeAction(actionId);
    } else {
      if (!canAddMore) {
        alert(`You can only add up to ${maxActions} quick actions.`);
        return;
      }
      addAction(actionId);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number, actionId: string) => {
    // Don't allow dragging permanent actions
    if (isActionPermanent(actionId)) {
      e.preventDefault();
      return;
    }
    setDraggedIndex(index);
    draggedItemRef.current = actionId;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", actionId);
    // Add a slight delay to show dragging state
    setTimeout(() => {
      (e.target as HTMLElement).style.opacity = "0.5";
    }, 0);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.target as HTMLElement).style.opacity = "1";
    setDraggedIndex(null);
    setDragOverIndex(null);
    draggedItemRef.current = null;
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    // Find how many permanent actions there are at the start
    const permanentCount = selectedActions.filter((a, i) => isActionPermanent(a.id)).length;

    // Don't allow dropping in the permanent actions area
    if (index < permanentCount) {
      return;
    }

    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Find how many permanent actions there are at the start
    const permanentCount = selectedActions.filter((a) => isActionPermanent(a.id)).length;

    // Don't allow dropping in the permanent actions area
    if (dropIndex < permanentCount) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Create a new array with reordered items
    const newActionIds = [...selectedActionIds];
    const [draggedItem] = newActionIds.splice(draggedIndex, 1);
    newActionIds.splice(dropIndex, 0, draggedItem);

    reorderActions(newActionIds);

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <>
      {/* General Settings Card */}
      <div className="card mb-5">
        <div className="card-header border-0 pt-5 pb-0">
          <h3 className="card-title fw-bold fs-4 m-0">
            <KTIcon iconName="setting-3" className="fs-3 me-2" />
            General Settings
          </h3>
        </div>
        <div className="card-body pt-5">
          {/* Sidebar State */}
          <div className="row align-items-center mb-5">
            <div className="col-lg-3">
              <label className="fw-semibold fs-6">Sidebar</label>
              <p className="text-gray-500 fs-7 mb-0">Default state</p>
            </div>
            <div className="col-lg-9">
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn btn-sm ${
                    sidebarState === "expanded" ? "btn-primary" : "btn-light-primary"
                  }`}
                  onClick={() => handleSidebarStateChange("expanded")}
                >
                  <KTIcon iconName="text-align-left" className="fs-5 me-1" />
                  Expanded
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    sidebarState === "collapsed" ? "btn-primary" : "btn-light-primary"
                  }`}
                  onClick={() => handleSidebarStateChange("collapsed")}
                >
                  <KTIcon iconName="text-align-justify-center" className="fs-5 me-1" />
                  Collapsed
                </button>
              </div>
            </div>
          </div>

          <div className="separator separator-dashed my-4"></div>

          {/* Table Page Size */}
          <div className="row align-items-center">
            <div className="col-lg-3">
              <label className="fw-semibold fs-6">Table Rows</label>
              <p className="text-gray-500 fs-7 mb-0">Default page size</p>
            </div>
            <div className="col-lg-9">
              <div className="btn-group" role="group">
                {([10, 25, 50, 100] as TablePageSize[]).map((size) => (
                  <button
                    key={size}
                    type="button"
                    className={`btn btn-sm ${
                      tablePageSize === size ? "btn-primary" : "btn-light-primary"
                    }`}
                    onClick={() => handleTablePageSizeChange(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Action Panel */}
      <div className="card">
        <div className="card-header border-0 pt-6 pb-0">
          <h3 className="card-title fw-bold fs-4 m-0">
            <KTIcon iconName="element-11" className="fs-3 me-2" />
            Quick Action Panel
          </h3>
          <div className="card-toolbar">
            <span className="badge badge-light-primary fs-6 px-4 py-2">
              {selectedActionIds.length} / {maxActions}
            </span>
          </div>
        </div>
        <div className="card-body pt-6">
          {/* Selected Actions */}
          {selectedActions.length > 0 && (
            <div className="bg-light-primary rounded p-6 mb-8">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                  <h5 className="fw-bold text-primary mb-1">Selected Quick Actions</h5>
                  <span className="text-gray-600 fs-7">Drag to reorder. These will appear in your header toolbar.</span>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => {
                    if (!canAddMore) {
                      alert(`You can only add up to ${maxActions} quick actions.`);
                      return;
                    }
                    addSeparator();
                  }}
                  disabled={!canAddMore}
                  title="Add a visual separator between buttons"
                >
                  <span className="fw-bold me-1">|</span> Add Separator
                </button>
              </div>
              <div className="d-flex flex-wrap gap-3">
                {selectedActions.map((action, index) => {
                  const isPermanent = isActionPermanent(action.id);
                  const isDragging = draggedIndex === index;
                  const isDragOver = dragOverIndex === index;

                  // Render separator badge
                  if (action.isSeparator) {
                    return (
                      <span
                        key={action.id}
                        draggable={true}
                        onDragStart={(e) => handleDragStart(e, index, action.id)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        className="badge badge-light-secondary fs-6 px-4 py-3 d-flex align-items-center gap-2 border border-secondary"
                        style={{
                          cursor: "grab",
                          transition: "all 0.15s ease",
                          transform: isDragOver ? "scale(1.05)" : undefined,
                          boxShadow: isDragOver ? "0 0 0 2px var(--kt-primary)" : undefined,
                        }}
                      >
                        <span className="fw-bold fs-3">|</span>
                        <span
                          className="cursor-pointer ms-1"
                          onClick={() => removeAction(action.id)}
                        >
                          <KTIcon iconName="cross" className="fs-5 text-secondary" />
                        </span>
                      </span>
                    );
                  }

                  // Render regular action badge
                  return (
                    <span
                      key={action.id}
                      draggable={!isPermanent}
                      onDragStart={(e) => handleDragStart(e, index, action.id)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      className="badge badge-light-primary fs-6 px-4 py-3 d-flex align-items-center gap-2 border border-primary"
                      style={{
                        cursor: isPermanent ? "default" : "grab",
                        transition: "all 0.15s ease",
                        transform: isDragOver ? "scale(1.05)" : undefined,
                        boxShadow: isDragOver ? "0 0 0 2px var(--kt-primary)" : undefined,
                      }}
                    >
                      <KTIcon iconName={action.icon} className="fs-4" />
                      {action.title}
                      {isPermanent ? (
                        <KTIcon iconName="lock" className="fs-5 text-primary" />
                      ) : (
                        <span
                          className="cursor-pointer ms-1"
                          onClick={() => removeAction(action.id)}
                        >
                          <KTIcon iconName="cross" className="fs-5 text-primary" />
                        </span>
                      )}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Available Actions Section */}
          <div>
            <h5 className="fw-bold text-gray-800 mb-1">Available Actions</h5>
            <span className="text-gray-600 fs-7 d-block mb-6">Click to add or remove from your quick actions panel.</span>

            {/* Available Actions by Category */}
            <div className="row g-5">
              {Object.entries(actionsByCategory).map(([category, actions]) => (
                <div key={category} className="col-12">
                  <div className="border border-gray-300 rounded p-5">
                    <label className="form-label fw-bold fs-6 text-gray-700 mb-4">
                      {category}
                    </label>
                    <div className="d-flex flex-wrap gap-3">
                      {actions.map((action) => {
                        const isSelected = isActionSelected(action.id);
                        return (
                          <span
                            key={action.id}
                            className={`badge fs-6 px-4 py-3 cursor-pointer d-flex align-items-center gap-2 ${
                              isSelected
                                ? "badge-primary"
                                : "badge-light hover-elevate-up"
                            }`}
                            onClick={() => handleToggleAction(action.id)}
                            title={action.description}
                            style={{ transition: "all 0.15s ease" }}
                          >
                            <KTIcon
                              iconName={action.icon}
                              className={`fs-4 ${isSelected ? "text-white" : ""}`}
                            />
                            {action.title}
                            {isSelected && (
                              <KTIcon iconName="check" className="fs-5 text-white" />
                            )}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export { UISettingsPage };
